import React, { Fragment, useEffect, useState } from 'react';

import styles from './styles.scss';
import {
  AppBar,
  AppBarContext,
  AppBarSection,
  AppBarTitle,
  Button,
  ButtonGroup,
  Checkbox,
  InputBox,
  RadioButton,
  Toaster,
  Icons,
} from '@densityco/ui';
import HorizontalForm from '../../../HorizontalForm';
import { SenseiApi } from '../../../../utils/api';
import { useRequiredSessionToken } from '../../../../utils/session-token';
import { convertMetersToInchesForDisplay } from '../../../../utils/measurement';
import Popup from '../../../Popup';
import FormLabel from '../../../FormLabel';
import { Sensor } from '../../../../types/api';

import MeasureHeightStore from '../../../../rx-stores/measure-height';
import {
  MeasureHeightActionTypes,
} from '../../../../types/measure-height';
import useRxStore from '../../../../utils/use-rx-store';
import useRxDispatch from '../../../../utils/use-rx-dispatch';

import { ResourceStatus } from '../../../../types/resource';

type MeasureHeightButtonProps = {
  sensor: {
    serial_number: Sensor['serial_number'];
  };
};

const MeasureHeightButton: React.FunctionComponent<MeasureHeightButtonProps> = ({
  sensor,
}) => {
  const state = useRxStore(MeasureHeightStore);
  const dispatch = useRxDispatch();

  const measureHeightButton = (
    <Button
      width="100%"
      variant={state.resource.status === ResourceStatus.COMPLETE ? 'default' : 'filled'}
      onClick={() => {
        dispatch({
          type: MeasureHeightActionTypes.MEASURE_HEIGHT_START,
          serialNumber: sensor.serial_number,
        });
      }}
      disabled={state.resource.status === ResourceStatus.LOADING}
      data-test-id="measure-height-start-button"
    >
      {state.resource.status === ResourceStatus.IDLE ? 'Measure Height' : null}
      {state.resource.status === ResourceStatus.LOADING ? 'Measuring - this may take a few minutes ...' : null}
      {state.resource.status === ResourceStatus.COMPLETE ? 'Measure Again' : null}
    </Button>
  );

  const parseMeasureHeightError = (error: Error): string => {
    // Override the throttling error message to make it a little more user-friendly.
    // Otherwise, return the raw error message.
    if (error.message.includes('A MEASURE_HEIGHT command is already queued for sensor')) {
      return 'Too many measure height commands were sent recently, please try again in a few minutes.';
    }
    return error.message;
  }


  return (
    <Fragment>
      <Popup
        target={
          <Button
            disabled={!sensor}
            data-test-id="measure-height-button"
            rightIcon={<Icons.ChevronDown />}
          >
            Measure Height
          </Button>
        }
        open={state.open}
        onOpen={() =>
          dispatch({ type: MeasureHeightActionTypes.MEASURE_HEIGHT_OPEN })
        }
        onClose={() =>
          dispatch({ type: MeasureHeightActionTypes.MEASURE_HEIGHT_CLOSE })
        }
        position={{ right: 0, top: 44 }}
        noPadding
      >
        <AppBarContext.Provider value="CARD_HEADER">
          <AppBar>
            <AppBarTitle>Measure Height</AppBarTitle>
          </AppBar>
        </AppBarContext.Provider>
        <div className={styles.body}>
          {state.resource.status === ResourceStatus.ERROR ? (
            <div className={styles.error} data-test-id="measure-height-error">{parseMeasureHeightError(state.resource.error)}</div>
          ) : null}

          {state.resource.status === ResourceStatus.COMPLETE ? (
            <Fragment>
              <FormLabel
                label="Height"
                htmlFor="measured-height"
                input={
                  <span data-test-id="measure-height-result">
                    {convertMetersToInchesForDisplay(parseFloat(state.resource.data.height))} inches
                  </span>
                }
              />
              <FormLabel label="Standard Deviation" input={state.resource.data.stddev} />

              <div className={styles.buttons}>
                {measureHeightButton}
              </div>
            </Fragment>
          ) : null}
          {state.resource.status === ResourceStatus.IDLE || state.resource.status === ResourceStatus.LOADING ? (
            <Fragment>
              <p>
                Make sure you're not standing under the unit{' '}
                <strong>{sensor.serial_number}</strong> or
                there's no obstructions blocking its view before measuring.{' '}
              </p>

              <div className={styles.buttons}>{measureHeightButton}</div>
            </Fragment>
          ) : null}
        </div>
      </Popup>
    </Fragment>
  );
};

export default MeasureHeightButton;
